import { useState } from "react";
import { Input as AntInput, Select as AntSelect, Space } from "antd";
import classNames from "classnames";
import { get, isNil } from "lodash";
import { BiChevronDown, BiChevronUp, BiTrash } from "react-icons/bi";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import Switch from "shared/components/Switch";
import { COLORS } from "shared/style/colors";
import { NODE_TYPES } from "utils/constants";
import { getValidatorsForInputType } from "utils/validation-rules";
import FieldConditionsEditor from "./FieldConditionsEditor";
import FieldInputConfigEditor from "./FieldInputConfigEditor";
import CanvasIcon from "./CanvasIcon";
import FailureModeField from "./FailureModeField";
import FieldRulesEditor from "./FieldRulesEditor";
import { applyVariableDefaults, buildFormFieldFromVariable } from "./catalog";
import { ICON_PATHS } from "./step-look";

const HTTP_METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE"];

const INPUT_TYPES = [
  "auto",
  "text",
  "date",
  "number",
  "slider",
  "email",
  "phone",
];

const VISIBLE_FIELDS = 6;

const DEFAULT_LIST_LABELS = { more: (count) => `+${count}` };

const keepCompatibleRules = (rules, type) => {
  const allowed = getValidatorsForInputType(type);
  return (rules || []).filter((rule) => allowed.includes(rule.validator));
};

export const FieldSection = ({ title, open, onToggle, children }) => (
  <div className="canvas-editor__subsection">
    <div
      className="canvas-editor__row-between canvas-editor__section-title"
      onClick={onToggle}
    >
      <span>{title}</span>
      {open ? <BiChevronUp size={16} /> : <BiChevronDown size={16} />}
    </div>
    {open ? children : null}
  </div>
);

const JsonField = ({ label, value, onChange }) => {
  const [draft, setDraft] = useState(
    isNil(value) ? "" : JSON.stringify(value, null, 2),
  );
  const [invalid, setInvalid] = useState(false);
  const handle = (event) => {
    const next = event.target.value;
    setDraft(next);
    if (next.trim() === "") {
      setInvalid(false);
      onChange(null);
      return;
    }
    try {
      onChange(JSON.parse(next));
      setInvalid(false);
    } catch (_) {
      setInvalid(true);
    }
  };
  return (
    <div>
      <div className="canvas-editor__label">{label}</div>
      <AntInput.TextArea
        autoSize={{ minRows: 3, maxRows: 10 }}
        value={draft}
        onChange={handle}
        status={invalid ? "error" : ""}
        className="canvas-editor__mono"
      />
    </div>
  );
};

export const FormDefinitionBuilder = ({
  formDefinition,
  variables,
  labels,
  strictLabels = {},
  listLabels = DEFAULT_LIST_LABELS,
  onChange,
}) => {
  const [openSections, setOpenSections] = useState({});
  const [editing, setEditing] = useState({});
  const [showAll, setShowAll] = useState(false);
  const [addingField, setAddingField] = useState(false);
  const base = formDefinition || { strict: true, fields: [] };
  const definition = { ...base, fields: base.fields || [] };
  const fields = definition.fields;
  const usedAccessors = new Set(fields.map((field) => field.accessor));
  const options = (variables || [])
    .filter((variable) => !usedAccessors.has(variable.accessor))
    .map((variable) => ({ value: variable.accessor, label: variable.label }));
  const fieldLabels = labels.FIELD_VALIDATION || labels;

  const emit = (next) => onChange(next);

  const toggleSection = (key) =>
    setOpenSections((current) => ({
      ...current,
      [key]: !current[key],
    }));

  const addField = (accessor) => {
    const variable = (variables || []).find(
      (item) => item.accessor === accessor,
    );
    if (!variable) return;
    emit({
      ...definition,
      fields: [...fields, buildFormFieldFromVariable(variable)],
    });
  };

  const updateField = (index, updater) =>
    emit({
      ...definition,
      fields: fields.map((field, i) => (i === index ? updater(field) : field)),
    });

  const removeField = (index) =>
    emit({ ...definition, fields: fields.filter((_, i) => i !== index) });

  const moveField = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= fields.length) return;
    const next = [...fields];
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved);
    emit({ ...definition, fields: next });
  };

  const hiddenCount = Math.max(fields.length - VISIBLE_FIELDS, 0);
  const shownFields = showAll ? fields : fields.slice(0, VISIBLE_FIELDS);
  const rulesSummary = (field) =>
    (field.rules || [])
      .map((rule) =>
        get(fieldLabels, ["VALIDATORS", rule.validator], rule.validator),
      )
      .join(", ");
  const toggleEditing = (key) =>
    setEditing((current) => ({ ...current, [key]: !current[key] }));

  return (
    <div className="canvas-questions">
      {fields.length === 0 ? (
        <p className="canvas-tab__hint">{labels.NO_FIELDS}</p>
      ) : null}
      {shownFields.map((field, index) => {
        const key = field.accessor || String(index);
        const summary = rulesSummary(field);
        const rebindOptions = (variables || [])
          .filter(
            (variable) =>
              variable.accessor === field.accessor ||
              !usedAccessors.has(variable.accessor),
          )
          .map((variable) => ({
            value: variable.accessor,
            label: variable.label,
          }));

        return (
          <div key={key} className="canvas-question">
            <div className="canvas-question__row">
              <span className="canvas-question__grip" aria-hidden="true">
                <CanvasIcon path={ICON_PATHS.grip} size={16} strokeWidth={3} />
              </span>
              <div className="canvas-question__text">
                <div className="canvas-question__title">
                  <strong>{field.label}</strong>
                  {field.required ? (
                    <span className="canvas-question__chip">
                      {listLabels.REQUIRED}
                    </span>
                  ) : null}
                  {field.display ? (
                    <span className="canvas-question__chip canvas-question__chip--muted">
                      {listLabels.DISPLAY}
                    </span>
                  ) : null}
                </div>
                <span className="canvas-question__accessor">
                  {field.accessor}
                </span>
                {summary ? (
                  <span className="canvas-question__rule">
                    {`${listLabels.CHECK} ${summary}`}
                  </span>
                ) : null}
              </div>
              <button
                type="button"
                className="canvas-question__edit"
                aria-label={listLabels.EDIT}
                aria-expanded={!!editing[key]}
                onClick={() => toggleEditing(key)}
              >
                <CanvasIcon
                  path={ICON_PATHS.pencil}
                  size={16}
                  strokeWidth={2}
                />
              </button>
            </div>
            {editing[key] ? (
              <div className="canvas-question__editor">
                <div className="canvas-editor__row-between mb-6">
                  <div className="canvas-editor__required-row">
                    <Switch
                      checked={!!field.display}
                      onChange={(checked) =>
                        updateField(index, (item) => ({
                          ...item,
                          display: checked,
                          required: checked ? false : item.required,
                        }))
                      }
                    />
                    <span className="canvas-editor__label">
                      {field.display
                        ? labels.FIELD_DISPLAY
                        : labels.FIELD_INPUT}
                    </span>
                  </div>
                  <Space size={4}>
                    <div
                      className="delete-icon"
                      onClick={() => moveField(index, -1)}
                    >
                      <BiChevronUp size={18} />
                    </div>
                    <div
                      className="delete-icon"
                      onClick={() => moveField(index, 1)}
                    >
                      <BiChevronDown size={18} />
                    </div>
                    <div
                      className="delete-icon"
                      onClick={() => removeField(index)}
                    >
                      <BiTrash color={COLORS.C_DANGER} size={18} />
                    </div>
                  </Space>
                </div>
                {!field.display ? (
                  <Space
                    direction="vertical"
                    size={8}
                    className="canvas-editor__full-width"
                  >
                    <Select
                      showSearch
                      label={labels.FIELD_BINDING}
                      optionFilterProp="label"
                      options={rebindOptions}
                      value={field.accessor}
                      onChange={(accessor) => {
                        const variable = (variables || []).find(
                          (item) => item.accessor === accessor,
                        );
                        if (!variable) return;
                        updateField(index, (item) =>
                          applyVariableDefaults(item, variable),
                        );
                      }}
                    />
                    {field.options && field.options.length ? null : (
                      <div>
                        <div className="canvas-editor__label">
                          {labels.FIELD_TYPE}
                        </div>
                        <AntSelect
                          className="canvas-editor__full-width"
                          value={field.type || "auto"}
                          options={INPUT_TYPES.map((type) => ({
                            value: type,
                            label: get(labels, ["FIELD_TYPES", type], type),
                          }))}
                          onChange={(type) =>
                            updateField(index, (item) => ({
                              ...item,
                              type: type === "auto" ? null : type,
                              inputConfig: {},
                              rules: keepCompatibleRules(item.rules, type),
                            }))
                          }
                        />
                      </div>
                    )}
                    <div className="canvas-editor__required-row">
                      <Switch
                        checked={!!field.required}
                        onChange={(checked) =>
                          updateField(index, (item) => ({
                            ...item,
                            required: checked,
                          }))
                        }
                      />
                      <span className="canvas-editor__label">
                        {labels.REQUIRED}
                      </span>
                    </div>
                    <FieldSection
                      title={fieldLabels.RULES_TITLE}
                      open={!!openSections[`${index}:rules`]}
                      onToggle={() => toggleSection(`${index}:rules`)}
                    >
                      <FieldRulesEditor
                        rules={field.rules || []}
                        variables={variables}
                        labels={fieldLabels}
                        fieldType={field.type || "auto"}
                        onChange={(rules) =>
                          updateField(index, (item) => ({ ...item, rules }))
                        }
                      />
                    </FieldSection>
                    <FieldSection
                      title={fieldLabels.INPUT_CONFIG_TITLE}
                      open={!!openSections[`${index}:input`]}
                      onToggle={() => toggleSection(`${index}:input`)}
                    >
                      <FieldInputConfigEditor
                        value={field.inputConfig || {}}
                        labels={fieldLabels}
                        inputType={field.type || "auto"}
                        onChange={(inputConfig) =>
                          updateField(index, (item) => ({
                            ...item,
                            inputConfig,
                          }))
                        }
                      />
                    </FieldSection>
                    <FieldSection
                      title={fieldLabels.VISIBLE_WHEN_TITLE}
                      open={!!openSections[`${index}:visible`]}
                      onToggle={() => toggleSection(`${index}:visible`)}
                    >
                      <FieldConditionsEditor
                        value={field.visibleWhen || []}
                        variables={variables}
                        labels={fieldLabels}
                        groupMode="flat"
                        onChange={(visibleWhen) =>
                          updateField(index, (item) => ({
                            ...item,
                            visibleWhen: Array.isArray(visibleWhen)
                              ? visibleWhen
                              : visibleWhen && visibleWhen.all
                                ? visibleWhen.all
                                : [],
                          }))
                        }
                      />
                    </FieldSection>
                  </Space>
                ) : null}
              </div>
            ) : null}
          </div>
        );
      })}
      {hiddenCount && !showAll ? (
        <button
          type="button"
          className="canvas-next__link"
          onClick={() => setShowAll(true)}
        >
          {listLabels.more(hiddenCount)}
        </button>
      ) : null}
      {addingField ? (
        <AntSelect
          autoFocus
          defaultOpen
          showSearch
          virtual={false}
          className="canvas-editor__full-width"
          placeholder={labels.ADD_FIELD}
          aria-label={labels.ADD_FIELD}
          value={null}
          options={options}
          optionFilterProp="label"
          onChange={(accessor) => {
            setAddingField(false);
            addField(accessor);
          }}
          onBlur={() => setAddingField(false)}
        />
      ) : (
        <button
          type="button"
          className="canvas-dashed-button"
          onClick={() => setAddingField(true)}
        >
          <CanvasIcon path={ICON_PATHS.plus} size={16} strokeWidth={2.2} />
          {listLabels.ADD}
        </button>
      )}
      <div
        className={classNames("canvas-strict", {
          "canvas-strict--on": !!definition.strict,
        })}
      >
        <div className="canvas-strict__text">
          <strong>{strictLabels.TITLE || labels.STRICT}</strong>
          <span className="canvas-strict__hint">
            {definition.strict ? strictLabels.ON : strictLabels.OFF}
          </span>
        </div>
        <Switch
          aria-label={strictLabels.TITLE || labels.STRICT}
          checked={!!definition.strict}
          onChange={(checked) => emit({ ...definition, strict: checked })}
        />
      </div>
    </div>
  );
};

const NodeConfigPanel = ({
  nodeType,
  config = {},
  labels,
  failureLabels,
  onConfigChange,
}) => {
  const setField = (key, value) =>
    onConfigChange({ ...(config || {}), [key]: value });

  if (nodeType === NODE_TYPES.API_CALL)
    return (
      <Space
        direction="vertical"
        size={12}
        className="canvas-editor__full-width"
      >
        <Input
          label={labels.URL}
          placeholder={labels.URL}
          value={get(config, "url", "")}
          onChange={(e) => setField("url", e.target.value)}
        />
        <Select
          label={labels.METHOD}
          placeholder={labels.METHOD}
          options={HTTP_METHODS.map((method) => ({
            value: method,
            label: method,
          }))}
          value={get(config, "method") || undefined}
          onChange={(value) => setField("method", value)}
        />
        <JsonField
          label={labels.HEADERS}
          value={get(config, "headers")}
          onChange={(value) => setField("headers", value)}
        />
        <JsonField
          label={labels.BODY}
          value={get(config, "body")}
          onChange={(value) => setField("body", value)}
        />
        <Input
          label={labels.RESULT_KEY}
          placeholder={labels.RESULT_KEY}
          value={get(config, "resultKey", "")}
          onChange={(e) => setField("resultKey", e.target.value)}
        />
        {failureLabels ? (
          <FailureModeField
            config={config}
            labels={failureLabels}
            onChange={(next) => onConfigChange(next)}
          />
        ) : null}
      </Space>
    );

  if (nodeType === NODE_TYPES.TIMER)
    return (
      <Input
        type="number"
        label={labels.DELAY_SECONDS}
        placeholder={labels.DELAY_SECONDS}
        value={isNil(get(config, "delaySeconds")) ? "" : config.delaySeconds}
        onChange={(e) =>
          setField(
            "delaySeconds",
            e.target.value === "" ? null : Number(e.target.value),
          )
        }
      />
    );

  if (nodeType === NODE_TYPES.COMMISSION)
    return (
      <Input
        type="number"
        label={labels.COMMISSION}
        placeholder={labels.COMMISSION}
        value={isNil(get(config, "commission")) ? "" : config.commission}
        onChange={(e) =>
          setField(
            "commission",
            e.target.value === "" ? null : Number(e.target.value),
          )
        }
      />
    );

  if (nodeType === NODE_TYPES.NOTIFICATION)
    return (
      <Space
        direction="vertical"
        size={8}
        className="canvas-editor__full-width"
      >
        <Input
          label={labels.CHANNEL}
          placeholder={labels.CHANNEL}
          value={get(config, "channel", "")}
          onChange={(e) => setField("channel", e.target.value)}
        />
        <Input
          label={labels.NOTIFICATION_TITLE}
          placeholder={labels.NOTIFICATION_TITLE}
          value={get(config, "title", "")}
          onChange={(e) => setField("title", e.target.value)}
        />
        <AntInput.TextArea
          rows={3}
          placeholder={labels.NOTIFICATION_CONTENT}
          value={get(config, "content", "")}
          onChange={(e) => setField("content", e.target.value)}
        />
      </Space>
    );

  return (
    <JsonField
      label={labels.JSON_CONFIG}
      value={config}
      onChange={(value) => onConfigChange(value || {})}
    />
  );
};

export default NodeConfigPanel;
